import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { GameSetupScreen } from '../components/GameSetupScreen'
import { GameRoomSidebar, GameRoomHeader } from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom'
import { useSidebar } from '../hooks/useSidebar'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { UserProfileManager } from '../utils/userProfile'
import '../styles/design-system.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-bottom-sheet.css'

type GameMode = 'select' | 'local' | 'network'
type GamePhase = 'setup' | 'lobby' | 'waiting' | 'playing' | 'finished'

export default function ExampleGame() {
  const [searchParams] = useSearchParams()
  const joinCode = searchParams.get('join')

  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('setup')

  // Local game
  const [currentTurn, setCurrentTurn] = useState<1 | 2>(1)
  const [score, setScore] = useState({ Player1: 0, Player2: 0 })

  // Network game
  const userProfile = UserProfileManager.get()
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)

  // Enhanced session for network mode
  const {
    session,
    createSession,
    joinSession,
    sendMove,
    onMessage,
  } = useEnhancedGameSession({
    sessionCode: undefined,
    playerNumber: undefined,
    playerName,
  })

  // Sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  // Derived state
  const sessionCode = session?.session_code
  const isHost = playerNumber === 1
  const player1 = session?.player1_name ? { name: session.player1_name } : null
  const player2 = session?.player2_name ? { name: session.player2_name } : null

  // Auto-join if joinCode is provided
  useEffect(() => {
    if (joinCode && gameMode === 'select') {
      setGameMode('network')
      setPhase('setup')
    }
  }, [joinCode, gameMode])

  // Listen for game state updates (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribe = onMessage('game_state', (data) => {
      console.log('[ExampleGame] Received game state:', data)
      // Update local state from network
      if (data.phase) setPhase(data.phase)
      if (data.currentTurn) setCurrentTurn(data.currentTurn)
      if (data.score) setScore(data.score)
    })

    return unsubscribe
  }, [gameMode, onMessage])

  // Listen for chat messages (network mode)
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribe = onMessage('chat', (data) => {
      console.log('[ExampleGame] Chat message:', data.message)
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return unsubscribe
  }, [gameMode, onMessage, playerNumber])

  // Broadcast state updates (network host)
  const broadcastState = (updates: any) => {
    if (gameMode === 'network' && isHost) {
      sendMove({
        type: 'game_state',
        ...updates,
      })
    }
  }

  // ========== HANDLERS ==========

  const handleModeSelect = (mode: 'local' | 'network' | 'ai') => {
    if (mode === 'ai') {
      alert('AI mode not implemented in this example')
      return
    }
    setGameMode(mode)
    setPhase(mode === 'local' ? 'playing' : 'setup')
  }

  const handleCreateNetwork = async () => {
    if (!playerName.trim()) {
      alert('Please enter your name')
      return
    }

    try {
      await createSession('example-game', playerName)
      setPlayerNumber(1)
      setPhase('lobby')
    } catch (error) {
      console.error('[ExampleGame] Failed to create session:', error)
      alert('Failed to create game. Please try again.')
    }
  }

  const handleJoinNetwork = async () => {
    const code = joinCode || prompt('Enter game code:')
    if (!code) return

    if (!playerName.trim()) {
      alert('Please enter your name')
      return
    }

    try {
      await joinSession(code, playerName)
      setPlayerNumber(2)
      setPhase('lobby')
    } catch (error) {
      console.error('[ExampleGame] Failed to join session:', error)
      alert('Failed to join game. Please check the code and try again.')
    }
  }

  const handleStartGame = () => {
    setPhase('playing')
    broadcastState({ phase: 'playing' })
  }

  const handlePlayTurn = () => {
    // Example: Increment score
    const newScore = { ...score }
    const playerKey = `Player${currentTurn}` as 'Player1' | 'Player2'
    newScore[playerKey] += 1

    // Switch turns
    const nextTurn = currentTurn === 1 ? 2 : 1
    setScore(newScore)
    setCurrentTurn(nextTurn)

    broadcastState({ score: newScore, currentTurn: nextTurn })
  }

  const handleReset = () => {
    setPhase('setup')
    setGameMode('select')
    setScore({ Player1: 0, Player2: 0 })
    setCurrentTurn(1)
  }

  // Handler for sidebar message sending
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (gameMode === 'network' && sessionCode) {
      // Send chat message via network session
      await sendMove({ type: 'chat', message })

      // Add to local chat
      const newMessage: Message = {
        sender: 'you',
        content: message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    }
  }, [gameMode, sessionCode, sendMove])

  // ========== RENDER ==========

  // 1. SETUP SCREEN (Mode Selection)
  if (gameMode === 'select') {
    return (
      <GameSetupScreen
        gameTitle="Example Game"
        gameIcon="🎮"
        gameBanner="var(--gradient-purple)"
        supportedModes={['local', 'network']}
        onModeSelect={handleModeSelect}
        onBack={() => (window.location.href = '/')}
      />
    )
  }

  // 2. NETWORK SETUP (Enter Name)
  if (gameMode === 'network' && phase === 'setup') {
    return (
      <div style={{ padding: 'var(--space-xl)', maxWidth: '400px', margin: '0 auto' }}>
        <h1>Enter Your Name</h1>
        <input
          type="text"
          className="input"
          placeholder="Your name"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          style={{ width: '100%', marginBottom: 'var(--space-md)' }}
        />
        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          <button className="btn btn-primary" onClick={handleCreateNetwork}>
            Create Game
          </button>
          <button className="btn btn-secondary" onClick={handleJoinNetwork}>
            Join Game
          </button>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setGameMode('select')}
          style={{ marginTop: 'var(--space-lg)' }}
        >
          ← Back
        </button>
      </div>
    )
  }

  // 3. WAITING SCREEN (Network Lobby)
  if (gameMode === 'network' && phase === 'lobby') {
    const bothConnected = player1 && player2

    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
        <GameRoomHeader
          gameTitle="Example Game"
          gameIcon="🎮"
          mode="network"
          players={[
            { name: player1?.name || 'Waiting...', status: player1 ? 'active' : 'disconnected' },
            { name: player2?.name || 'Waiting...', status: player2 ? 'active' : 'disconnected' },
          ]}
          sessionCode={sessionCode}
          onBack={() => setGameMode('select')}
        />

        <div style={{ padding: 'var(--space-xl)', textAlign: 'center' }}>
          <div className="card" style={{ maxWidth: '600px', margin: '0 auto', padding: 'var(--space-2xl)' }}>
            <h2 style={{ marginBottom: 'var(--space-lg)' }}>Waiting for Players</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-xl)' }}>
              {bothConnected ? 'Both players connected!' : 'Waiting for opponent to join...'}
            </p>

            <div style={{ marginBottom: 'var(--space-xl)' }}>
              <p><strong>Player 1:</strong> {player1?.name || 'Waiting...'}</p>
              <p><strong>Player 2:</strong> {player2?.name || 'Waiting...'}</p>
            </div>
          </div>

          {/* Sidebar for lobby chat */}
          {sessionCode && (
            <div style={{ marginTop: 'var(--space-xl)', maxWidth: '600px', margin: '0 auto' }}>
              <GameRoomSidebar
                mode="network"
                gameState={{
                  phase: 'Waiting for players',
                  turn: bothConnected ? 'Ready to start' : 'Waiting...'
                }}
                messages={chatMessages}
                historyItems={historyItems}
                onSendMessage={handleSidebarMessage}
                collapsed={false}
                position="docked"
                quickReactions={true}
              />
            </div>
          )}

          {/* Start Button (Host Only) */}
          {isHost && bothConnected && (
            <div style={{ textAlign: 'center', marginTop: 'var(--space-xl)' }}>
              <button className="btn btn-primary btn-lg" onClick={handleStartGame}>
                Start Game
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // 4. PLAYING SCREEN
  if (phase === 'playing') {
    const isLocalMode = gameMode === 'local'
    const playingPlayer1 = isLocalMode
      ? { number: 1, name: 'Player 1', isConnected: true }
      : { number: 1, name: player1?.name || 'Player 1', isConnected: !!player1 }
    const playingPlayer2 = isLocalMode
      ? { number: 2, name: 'Player 2', isConnected: true }
      : { number: 2, name: player2?.name || 'Player 2', isConnected: !!player2 }

    return (
      <div className="examplegame-container universal-game-room">
        <GameRoomHeader
          gameTitle="Example Game"
          gameIcon="🎮"
          mode={gameMode as 'local' | 'network'}
          players={[
            { name: playingPlayer1.name, status: 'active' },
            { name: playingPlayer2.name, status: 'active' },
          ]}
          sessionCode={sessionCode}
          onBack={() => setGameMode('select')}
        />

        <div className="game-room-content">
          <div className="game-board-area">
            <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              <h2 style={{ marginBottom: 'var(--space-lg)' }}>Game Board</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-xl)' }}>
                This is where your game content goes
              </p>

              <div style={{ marginTop: 'var(--space-xl)' }}>
                <h3>Current Turn: Player {currentTurn}</h3>
                <p>Score: Player 1: {score.Player1} | Player 2: {score.Player2}</p>

                {(!isHost || currentTurn === playerNumber) && (
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handlePlayTurn}
                    style={{ marginTop: 'var(--space-lg)' }}
                  >
                    Take Turn (+1 Point)
                  </button>
                )}
              </div>

              <button
                className="btn btn-secondary"
                onClick={handleReset}
                style={{ marginTop: 'var(--space-2xl)' }}
              >
                End Game
              </button>
            </div>
          </div>

          <GameRoomSidebar
            mode="network"
            gameState={{
              phase: 'Playing',
              turn: `Player ${currentTurn}`,
              score: score,
            }}
            messages={chatMessages}
            historyItems={historyItems}
            onSendMessage={handleSidebarMessage}
            collapsed={sidebar.collapsed}
            onToggleCollapse={sidebar.toggle}
            position={sidebar.position}
            quickReactions={true}
          />
        </div>
      </div>
    )
  }

  return null
}
